import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Check, Loader2, Package, Scan } from 'lucide-react';
import StaffScanGate from '@/components/StaffScanGate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { errText } from '@/services/tissueJwApi';

const BOX_STATUS = { OPEN: 'Open', READY_FOR_INTERNAL_TRANSFER: 'Closed · waiting for Internal Transfer' };
const GONE = ['TRANSFERRED', 'TRANSFERRED_TO_DISPATCH'];

const BoxesTable = ({ boxes, active, onActivate, onClose }) => (
  <Table>
    <TableHeader><TableRow><TableHead>Box</TableHead><TableHead>FG</TableHead><TableHead>Status</TableHead>
      <TableHead className="text-right">Packs</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
    <TableBody>
      {!boxes.length && <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-6">No boxes yet</TableCell></TableRow>}
      {boxes.map((b) => (
        <TableRow key={b.box_barcode} data-testid={`box-row-${b.box_barcode}`}>
          <TableCell className="font-mono text-xs">{b.box_barcode}</TableCell>
          <TableCell className="text-xs">{b.fg_name ? `${b.fg_name} · ${b.design_name || ''}` : '—'}</TableCell>
          <TableCell><Badge variant="outline" className="text-[10px]" data-testid={`box-status-${b.box_barcode}`}>{BOX_STATUS[b.status] || b.status}</Badge></TableCell>
          <TableCell className="text-right">{b.usn_count}</TableCell>
          <TableCell className="text-right space-x-1">
            {b.status === 'OPEN' && (<>
              <Button size="sm" variant={active === b.box_barcode ? 'default' : 'outline'} onClick={() => onActivate(b.box_barcode)}
                data-testid={`activate-box-${b.box_barcode}`}>{active === b.box_barcode ? 'Scanning' : 'Scan into'}</Button>
              <Button size="sm" variant="ghost" onClick={() => onClose(b.box_barcode)} data-testid={`close-box-${b.box_barcode}`}><Check className="w-3.5 h-3.5" /></Button>
            </>)}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const TissueAssortment = () => {
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState([]);
  const [active, setActive] = useState('');
  const [boxInput, setBoxInput] = useState('');
  const [pack, setPack] = useState('');
  const [busy, setBusy] = useState('');
  const packRef = useRef(null);

  const load = () => apiClient.get('/tissue/assortment/boxes', { params: { mode: 'PACKS' } }).then((r) => setBoxes((r.data.boxes || []).filter((b) => !GONE.includes(b.status))));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (active) setTimeout(() => packRef.current?.focus(), 50); }, [active]);

  const act = async (key, fn) => {
    setBusy(key);
    try { await fn(); await load(); } catch (e) { toast.error(errText(e, 'Failed')); } finally { setBusy(''); }
  };
  const openBox = () => boxInput.trim() ? act('open', async () => {
    const r = await apiClient.post('/tissue/assortment/boxes', { box_barcode: boxInput.trim() });
    toast.success(`Box ${r.data.box_barcode} opened`); setActive(r.data.box_barcode); setBoxInput('');
  }) : toast.error('Scan the BUN bundle barcode for this box');
  const scan = (e) => { e.preventDefault(); const code = pack.trim(); if (!code || !active) return;
    setPack('');
    act('scan', async () => { const r = await apiClient.post('/tissue/assortment/scan-usn', { box_barcode: active, usn_code: code });
      toast.success(`${r.data.pack_code} boxed (${r.data.usn_count})`); }).then(() => setTimeout(() => packRef.current?.focus(), 50)); };
  const closeBox = (code) => act('close', async () => { await apiClient.post(`/tissue/assortment/boxes/${encodeURIComponent(code)}/close`);
    toast.success(`Box ${code} closed — send it via Internal Transfer`); if (active === code) setActive(''); });

  return (
    <StaffScanGate stationName="Tissue — Assortment Packing">
      <div className="min-h-screen bg-[#FFFBF5]" data-testid="tissue-assortment">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
          <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/sections/tissue')} className="text-[#F9FAFB] hover:bg-white/10"><ArrowLeft className="w-5 h-5" /></Button>
              <div><h1 className="text-xl font-bold font-display">Tissue — Assortment Packing</h1>
                <p className="text-sm text-[#E8DCC8]">Scan received FG packs into boxes (one FG per box) → close → send via Internal Transfer</p></div>
            </div>
            <Button variant="secondary" onClick={() => navigate('/sections/tissue/internal-transfer')} data-testid="go-internal-transfer-btn">
              <ArrowRightLeft className="w-4 h-4 mr-2" /> Internal Transfer</Button>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Open a new box</CardTitle></CardHeader>
            <CardContent className="flex gap-2 items-end">
              <div><Label className="text-xs">Box bundle barcode (scan the BUN sticker — one bundle = one box)</Label>
                <Input value={boxInput} onChange={(e) => setBoxInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && openBox()}
                  className="w-[280px] mt-1" placeholder="BUN000001/26-27" data-testid="new-box-input" /></div>
              <Button onClick={openBox} disabled={busy === 'open'} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="open-box-btn">
                {busy === 'open' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}<span className="ml-2">Open box</span></Button>
            </CardContent>
          </Card>
          {active && (
            <Card className="border-[#2D5F4F]/40">
              <CardHeader><CardTitle className="text-base">Scanning packs into {active}</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={scan} className="flex items-center gap-2">
                  <Scan className="w-4 h-4 text-gray-500" />
                  <Input ref={packRef} value={pack} onChange={(e) => setPack(e.target.value)} placeholder="Scan pack barcode (TP…)"
                    className="h-10 max-w-md" disabled={busy === 'scan'} data-testid="scan-pack-input" />
                  <Button type="submit" disabled={busy === 'scan'} data-testid="scan-pack-submit">{busy === 'scan' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}</Button>
                </form>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader><CardTitle className="text-base">Boxes ({boxes.length})</CardTitle></CardHeader>
            <CardContent><BoxesTable boxes={boxes} active={active} onActivate={setActive} onClose={closeBox} /></CardContent>
          </Card>
        </main>
      </div>
    </StaffScanGate>
  );
};

export default TissueAssortment;
